// Wave 20.5 — DropsWidget
//
// Floating call-to-action pill in the bottom-right of the homepage
// that funnels curious visitors to the /drops marketing page. Drops
// are the entry-tier product (small, shareable, viral loop); the
// widget is intentionally small so it doesn't distract from the
// primary Aero AI pitch above.
//
// Behavior:
//   - Idle: 52px round button with an upward-share icon.
//   - Hover: expands to a chat-bubble-shaped pill with "Drop your
//     peaks →" copy. Reserved height so layout doesn't reflow.
//   - Idle pulse: gentle box-shadow ring 2s cycle so the button
//     catches peripheral vision without being obnoxious.
//   - Scroll behavior: hides when scrolling down, reappears on
//     scroll up. Standard chat-widget pattern.
//   - Click: navigates to /drops.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, keyframes } from '@mui/material';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useNavigate } from 'react-router-dom';
import { colors } from '../theme';

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(106, 255, 69, 0.55); }
  70%  { box-shadow: 0 0 0 18px rgba(106, 255, 69, 0); }
  100% { box-shadow: 0 0 0 0 rgba(106, 255, 69, 0); }
`;

export default function DropsWidget() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const dy = y - lastY.current;
      // Ignore tiny movements; require a real swipe of intent.
      if (Math.abs(dy) < 8) return;
      setVisible(dy < 0 || y < 40);
      lastY.current = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label="Learn about Drop Links"
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
      onFocus={() => setExpanded(true)}
      onBlur={() => setExpanded(false)}
      onClick={() => navigate('/drops')}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate('/drops');
        }
      }}
      sx={{
        position: 'fixed',
        bottom: { xs: 20, sm: 28 },
        right: { xs: 16, sm: 28 },
        zIndex: 1200,
        height: 52,
        borderRadius: 26,
        backgroundColor: colors.brand,
        color: '#0A100C',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: expanded ? 'flex-start' : 'center',
        px: expanded ? 2.5 : 0,
        // Locked min-width for the tap target; expand to fit copy.
        width: expanded ? 220 : 52,
        overflow: 'hidden',
        transition:
          'width 220ms cubic-bezier(0.22, 0.61, 0.36, 1), padding 220ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 300ms, opacity 300ms',
        transform: visible ? 'translateY(0)' : 'translateY(90px)',
        opacity: visible ? 1 : 0,
        boxShadow: '0 6px 18px rgba(0, 0, 0, 0.35)',
        // Pulse ring — only when idle so hover state is calm.
        animation: expanded ? 'none' : `${pulse} 2.4s ease-out infinite`,
        // On very narrow screens, don't expand — keep the round tap target.
        '@media (max-width: 480px)': {
          width: 52,
          padding: 0,
          justifyContent: 'center',
        },
      }}
    >
      <IosShareIcon sx={{ fontSize: 24, flexShrink: 0 }} />
      {expanded && (
        <Typography
          sx={{
            ml: 1.25,
            fontWeight: 800,
            fontSize: '0.95rem',
            whiteSpace: 'nowrap',
          }}
        >
          Drop your friends →
        </Typography>
      )}
    </Box>
  );
}
